/**
 * Registration Modal Module
 * Handles the registration request modal form submission
 */

import { showResultModal } from './result-modal.js';
import { trackModalOpen, trackModalClose, trackFormSubmit } from './gtm-events.js';

// Public HubSpot Forms API configuration. Portal and form IDs are public by design.
const HUBSPOT_PORTAL_ID = '146450502';
const HUBSPOT_FORM_ID = '1b9ebe6c-fba3-417b-8c5f-b13678696813';
const REGISTRATION_ENDPOINT = `https://api-eu1.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;

// Form validation patterns (matching React app)
const PATTERNS = {
  NAME: /^[a-zA-Zа-яА-ЯіїєґІЇЄҐʼ\s.\-_]+$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\d{5,15}$/ // International phone numbers: 5-15 digits
};

// State
let isSubmitting = false;
let isFormDirty = false;

/**
 * Initialize the registration modal
 */
export function initRegistrationModal() {
  const modal = document.getElementById('registration-modal');
  if (!modal) return;

  const form = document.getElementById('registration-form');
  const triggers = document.querySelectorAll('[data-registration-modal]');
  const closeButtons = modal.querySelectorAll('[data-modal-close]');

  // Open modal from trigger buttons
  triggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(modal);
    });
  });

  // Handle Pro signup form (email capture → open modal with email pre-filled)
  const proForms = document.querySelectorAll('[data-pro-form]');
  proForms.forEach(proForm => {
    proForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const emailInput = proForm.querySelector('input[name="email"]');
      const email = emailInput?.value?.trim() || '';
      openModal(modal, email);
    });
  });

  // Close modal
  closeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      closeModal(modal);
    });
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) {
      closeModal(modal);
    }
  });

  // Form submission
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSubmit(modal, form);
    });
  }

  // Listen for reopen event from result modal "Try Again" button
  window.addEventListener('reopenRegistrationModal', () => {
    openModal(modal);
  });

  // Phone input formatting
  const phoneInput = document.getElementById('reg-phone');
  if (phoneInput) {
    phoneInput.addEventListener('input', formatPhoneInput);
  }

  // Track form dirty state and clear field errors on input
  if (form) {
    const formInputs = form.querySelectorAll('input, textarea, select');
    formInputs.forEach(input => {
      input.addEventListener('input', () => {
        isFormDirty = true;
        // Clear error for this specific field when user types
        if (input.name) {
          clearFieldError(form, input.name);
        }
      });
      input.addEventListener('change', () => {
        isFormDirty = true;
      });
    });
  }
}

/**
 * Open the modal
 * @param {HTMLElement} modal - The modal element
 * @param {string} [prefillEmail] - Optional email to pre-fill in the form
 */
function openModal(modal, prefillEmail = '') {
  modal.hidden = false;
  document.body.style.overflow = 'hidden';

  // Track modal open in GTM
  trackModalOpen('registration');

  // Reset to form state
  showFormState(modal);

  // Pre-fill email if provided
  if (prefillEmail) {
    const emailInput = modal.querySelector('input[name="email"]');
    if (emailInput) {
      emailInput.value = prefillEmail;
    }
  }

  // Focus first empty input, or first input if all filled
  const inputs = modal.querySelectorAll('input:not([type="hidden"])');
  let focusTarget = null;
  for (const input of inputs) {
    if (!input.value) {
      focusTarget = input;
      break;
    }
  }
  focusTarget = focusTarget || inputs[0];
  if (focusTarget) {
    setTimeout(() => focusTarget.focus(), 100);
  }

  // Trap focus inside modal
  trapFocus(modal);
}

/**
 * Close the modal
 * @param {HTMLElement} modal - The modal element
 * @param {boolean} [force=false] - Force close without confirmation
 * @param {boolean} [preserveData=false] - Keep entered values for a retry
 */
function closeModal(modal, force = false, preserveData = false) {
  // Check if form has unsaved changes
  if (isFormDirty && !force) {
    const confirmMessage = getConfirmationMessage();
    if (!confirm(confirmMessage)) {
      return; // User cancelled, don't close
    }
  }

  modal.hidden = true;
  document.body.style.overflow = '';

  // Track modal close in GTM (only if user actively closed, not after submit)
  if (!force) {
    trackModalClose('registration');
  }

  if (!preserveData) {
    // Reset form state
    const form = document.getElementById('registration-form');
    if (form) {
      form.reset();
      clearErrors(form);
    }
  }

  // A reopened retry form starts from the preserved values as its new baseline.
  isFormDirty = false;
}

/**
 * Get confirmation message based on locale
 * @returns {string} - Localized confirmation message
 */
function getConfirmationMessage() {
  const isUkrainian = window.location.pathname.startsWith('/ua');
  return isUkrainian
    ? 'У вас є незбережені дані. Ви впевнені, що хочете закрити форму?'
    : 'You have unsaved changes. Are you sure you want to close this form?';
}

/**
 * Handle form submission
 */
async function handleSubmit(modal, form) {
  if (isSubmitting) return;

  // Clear previous errors
  clearErrors(form);

  // Validate form
  const formData = new FormData(form);
  const data = Object.fromEntries(formData);

  const errors = validateForm(data);
  if (Object.keys(errors).length > 0) {
    showErrors(form, errors);
    return;
  }

  const phone = `+${data.country_code}${data.phone_number.replace(/\s/g, '')}`;
  const fields = [
    { objectTypeId: '0-1', name: 'firstname', value: data.full_name.trim() },
    { objectTypeId: '0-1', name: 'email', value: data.email.trim() },
    { objectTypeId: '0-1', name: 'phone', value: phone },
    { objectTypeId: '0-2', name: 'name', value: data.business_name.trim() }
  ];

  if (data.number_of_employees) {
    fields.push({
      objectTypeId: '0-2',
      name: 'numberofemployees',
      value: data.number_of_employees
    });
  }

  const payload = {
    submittedAt: Date.now(),
    fields,
    context: {
      pageUri: window.location.href,
      pageName: document.title
    },
    skipValidation: true
  };

  isSubmitting = true;
  showLoadingState(modal);

  try {
    const response = await fetch(REGISTRATION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'HubSpot form submission failed');
    }

    // Clear dirty state on successful submission
    isFormDirty = false;

    // Track successful form submission in GTM
    trackFormSubmit('registration', true);

    // Close registration modal and show success result modal
    closeModal(modal, true);
    showResultModal('success');
  } catch (error) {
    // Track failed form submission in GTM
    trackFormSubmit('registration', false);

    // Preserve the clinic's data so the request can be retried without retyping.
    closeModal(modal, true, true);
    showResultModal('error', error.message);
  } finally {
    isSubmitting = false;
  }
}

/**
 * Validate form data
 */
function validateForm(data) {
  const errors = {};
  const lang = data.locale || 'en';

  // Get error messages based on locale
  const messages = getValidationMessages(lang);

  // Full name validation
  if (!data.full_name?.trim()) {
    errors.full_name = messages.fullNameEmpty;
  } else if (!PATTERNS.NAME.test(data.full_name.trim())) {
    errors.full_name = messages.fullNameInvalid;
  }

  // Email validation
  if (!data.email?.trim()) {
    errors.email = messages.emailEmpty;
  } else if (!PATTERNS.EMAIL.test(data.email.trim())) {
    errors.email = messages.emailInvalid;
  }

  // Veterinary business name validation
  if (!data.business_name?.trim()) {
    errors.business_name = messages.businessNameEmpty;
  }

  // Optional employee count must be a positive whole number
  if (data.number_of_employees) {
    const employeeCount = Number(data.number_of_employees);
    if (!Number.isInteger(employeeCount) || employeeCount < 1) {
      errors.number_of_employees = messages.employeeCountInvalid;
    }
  }

  // Phone validation
  const phone = data.phone_number?.replace(/\s/g, '') || '';
  if (!phone) {
    errors.phone_number = messages.phoneEmpty;
  } else if (!PATTERNS.PHONE.test(phone)) {
    errors.phone_number = messages.phoneInvalid;
  }

  return errors;
}

/**
 * Get validation messages based on locale
 */
function getValidationMessages(lang) {
  const messages = {
    en: {
      fullNameEmpty: 'Full name is required',
      fullNameInvalid: 'Please enter a valid name',
      emailEmpty: 'Email is required',
      emailInvalid: 'Please enter a valid email',
      phoneEmpty: 'Phone number is required',
      phoneInvalid: 'Please enter a valid phone number',
      businessNameEmpty: 'Clinic or business name is required',
      employeeCountInvalid: 'Please enter a valid number of employees'
    },
    ua: {
      fullNameEmpty: 'Введіть повне імʼя',
      fullNameInvalid: 'Введіть коректне імʼя',
      emailEmpty: 'Введіть email',
      emailInvalid: 'Введіть коректний email',
      phoneEmpty: 'Введіть номер телефону',
      phoneInvalid: 'Введіть коректний номер телефону',
      businessNameEmpty: 'Введіть назву ветеринарного бізнесу',
      employeeCountInvalid: 'Введіть коректну кількість працівників'
    }
  };

  return messages[lang] || messages.en;
}

/**
 * Show form validation errors
 */
function showErrors(form, errors) {
  Object.entries(errors).forEach(([field, message]) => {
    const errorEl = form.querySelector(`[data-error="${field}"]`);
    const inputEl = form.querySelector(`[name="${field}"]`);

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('form-group__error--visible');
    }

    if (inputEl) {
      inputEl.classList.add('input--error');
      inputEl.setAttribute('aria-invalid', 'true');
    }
  });

  // Focus first error field
  const firstErrorField = form.querySelector('.input--error');
  if (firstErrorField) {
    firstErrorField.focus();
  }
}

/**
 * Clear form validation errors
 */
function clearErrors(form) {
  const errorEls = form.querySelectorAll('[data-error]');
  const inputEls = form.querySelectorAll('.input--error');

  errorEls.forEach(el => {
    el.textContent = '';
    el.classList.remove('form-group__error--visible');
  });

  inputEls.forEach(el => {
    el.classList.remove('input--error');
    el.removeAttribute('aria-invalid');
  });
}

/**
 * Clear validation error for a specific field
 * @param {HTMLFormElement} form - The form element
 * @param {string} fieldName - The name attribute of the field to clear
 */
function clearFieldError(form, fieldName) {
  const inputEl = form.querySelector(`[name="${fieldName}"]`);
  const errorEl = form.querySelector(`[data-error="${fieldName}"]`);

  if (inputEl) {
    inputEl.classList.remove('input--error');
    inputEl.removeAttribute('aria-invalid');
  }

  if (errorEl) {
    errorEl.textContent = '';
    errorEl.classList.remove('form-group__error--visible');
  }
}

/**
 * Show different modal states
 */
function showFormState(modal) {
  const form = document.getElementById('registration-form');
  const loading = document.getElementById('registration-loading');

  if (form) form.hidden = false;
  if (loading) loading.hidden = true;
}

function showLoadingState(modal) {
  const form = document.getElementById('registration-form');
  const loading = document.getElementById('registration-loading');

  if (form) form.hidden = true;
  if (loading) loading.hidden = false;
}

/**
 * Format phone input - allows international phone numbers
 */
function formatPhoneInput(e) {
  // Remove non-digits
  let value = e.target.value.replace(/\D/g, '');

  // Limit to 15 digits (max international phone number length)
  value = value.slice(0, 15);

  // Don't apply specific formatting - just clean the input
  // Different countries have different formats
  e.target.value = value;
}

/**
 * Trap focus inside modal for accessibility
 */
function trapFocus(modal) {
  const focusableElements = modal.querySelectorAll(
    'button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  modal.addEventListener('keydown', function handleTab(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  });
}
